/* ═══════════════════════════════════════════════════════════════
   Project spec Publish gate — the pure reads (docs/SYNC_PLAN.md D12).

   A project spec has a live draft (`coreContent`, `systems`) and, once
   someone has used Publish, a published copy (`publishedContent`,
   `publishedSystems`). Exports and issuances read the PUBLISHED view; a
   spec that was never published exports its live content (first Publish
   = current live, so nothing already exported changes).
   ═══════════════════════════════════════════════════════════════ */

import type { ContentBlock, ProjectSpec, SystemModule } from './spec';
import { stableStringify } from './spec-compose';

type PublishFields = Pick<
  ProjectSpec,
  'coreContent' | 'systems' | 'publishedContent' | 'publishedSystems' | 'publishedVersion'
>;

/** Has this spec ever been through Publish? */
export function isProjectSpecPublished(spec: Pick<ProjectSpec, 'publishedVersion'>): boolean {
  return (spec.publishedVersion ?? 0) > 0;
}

/** What exports and issuance snapshots read: published when there is one,
 *  live otherwise. */
export function publishedView(spec: Partial<PublishFields>): {
  coreContent: ContentBlock[];
  systems: SystemModule[];
} {
  return {
    coreContent: spec.publishedContent ?? spec.coreContent ?? [],
    systems: spec.publishedSystems ?? spec.systems ?? [],
  };
}

const pendingCache = new WeakMap<object, boolean>();

/** Published at least once AND the live draft now differs from it. */
export function isProjectSpecPending(spec: Partial<PublishFields>): boolean {
  if (!spec.publishedContent || (spec.publishedVersion ?? 0) <= 0) return false;
  const hit = pendingCache.get(spec);
  if (hit !== undefined) return hit;
  const differs =
    stableStringify(spec.coreContent ?? []) !== stableStringify(spec.publishedContent) ||
    stableStringify(spec.systems ?? []) !== stableStringify(spec.publishedSystems ?? []);
  pendingCache.set(spec, differs);
  return differs;
}
